import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Target, Rocket, Check, Users, BarChart3, Cog } from 'lucide-react';

const IAPage = () => {
  const offers = [
    {
      id: 1,
      icon: Lightbulb,
      title: 'AI BrainStorm',
      subtitle: 'Workshop "Déclic IA"',
      format: '1 journée / 2 demi-journées',
      description: 'Sensibilisez vos équipes dirigeantes à l\'IA et identifiez rapidement vos premiers cas d\'usage à fort potentiel.',
      features: [
        'Entretiens avec opérationnels, Managers et C-Level',
        'Sensibilisation COMEX / CODIR',
        'Démo de cas concrets',
        'Identification rapide de 5 use cases'
      ],
      deliverables: [
        'Vision partagée sur le potentiel IA',
        'Liste de 5 cas d\'usage identifiés',
        'Premiers quick wins identifiés'
      ],
      cta: 'Organiser un workshop'
    },
    {
      id: 2,
      icon: Target,
      title: 'AI Design',
      subtitle: 'Cadrage & Priorisation',
      format: '4 à 6 semaines',
      description: 'Identifiez et priorisez les meilleurs cas d\'usage IA, puis lancez vos premiers projets avec une feuille de route claire.',
      features: [
        {
          title: '1. Audit IA & opportunités',
          duration: '1-2 semaines',
          items: [
            'Interviews direction + opérationnels',
            'Analyse des process',
            'Identification des tâches chronophages et inefficacités',
            'Cartographie des opportunités IA'
          ]
        },
        {
          title: '2. Sélection & priorisation',
          duration: '',
          items: [
            'Scoring : ROI, complexité, faisabilité, time-to-value',
            'Top 3 à 5 use cases priorisés',
            'Business case (gain estimé, coût, délai)'
          ]
        },
        {
          title: '3. Choix des outils & architecture',
          duration: '',
          items: [
            'LLM (OpenAI, Mistral…)',
            'No-code / automation (Make, Zapier…)',
            'Outils métier',
            'Stack recommandée et schéma d\'architecture'
          ]
        },
        {
          title: '4. Cadrage projet',
          duration: '',
          items: [
            'Roadmap 3 mois / 6 mois',
            'Planning et RACI',
            'Gouvernance (COPIL, COMEX)',
            'Plan de déploiement clair'
          ]
        }
      ],
      deliverables: [
        'Cartographie des opportunités IA',
        'Business cases des use cases prioritaires',
        'Architecture cible recommandée',
        'Feuille de route détaillée'
      ],
      cta: 'Cadrer mon projet IA',
      featured: true
    },
    {
      id: 3,
      icon: Rocket,
      title: 'AI Transformation',
      subtitle: 'Pilotage de programme IA',
      format: 'Mission longue durée',
      description: 'Bénéficiez d\'un accompagnement complet pour déployer l\'IA à l\'échelle et transformer durablement vos opérations.',
      features: [
        {
          title: 'Direction de programme',
          items: [
            'Rôle de "Head of AI / Transformation"',
            'Animation COPIL',
            'Suivi KPI'
          ]
        },
        {
          title: 'Déploiement',
          items: [
            'Déploiement des use cases',
            'Automatisation des process',
            'Agents IA & Copilotes Métier'
          ]
        },
        {
          title: 'Conduite du changement',
          items: [
            'Accompagnement au changement',
            'Formation équipes',
            'Adoption et documentation'
          ]
        },
        {
          title: 'Performance',
          items: [
            'Mesure de la performance',
            'KPI avant / après',
            'ROI réel et amélioration continue'
          ]
        }
      ],
      deliverables: [
        'Programme IA déployé et opérationnel',
        'Équipes formées et autonomes',
        'ROI mesuré et documenté',
        'Gouvernance IA pérenne'
      ],
      cta: 'Piloter ma transformation IA'
    }
  ];

  return (
    <div data-testid="ia-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#0a1628] text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&auto=format&fit=crop&q=60')" 
          }}
        />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <p className="caption-text text-[#BFA15F] mb-4">Intelligence Artificielle</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] font-heading text-white mb-6">
              Accélérez votre<br />
              <span className="text-[#BFA15F]">transformation IA</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              De la sensibilisation au déploiement à l'échelle, je vous accompagne 
              pour exploiter le potentiel de l'IA et créer de la valeur concrète.
            </p>
            <div className="flex flex-wrap gap-4">
              <span className="px-4 py-2 border border-slate-500 text-sm text-slate-300">Audit & Stratégie</span>
              <span className="px-4 py-2 border border-slate-500 text-sm text-slate-300">Cadrage projet</span>
              <span className="px-4 py-2 border border-[#BFA15F] text-sm text-[#BFA15F]">Pilotage programme</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-6">
              3 offres pour accompagner<br />
              <span className="text-slate-400">votre maturité IA</span>
            </h2>
            <p className="body-large">
              Que vous débutiez votre réflexion ou que vous souhaitiez passer à l'échelle, 
              une offre adaptée à votre niveau de maturité et à vos ambitions.
            </p>
          </div>
        </div>
      </section>

      {/* Offers */}
      <section data-testid="ia-offers-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-8">
            {offers.map((offer) => (
              <div 
                key={offer.id}
                data-testid={`ia-offer-${offer.id}`}
                className={`bg-white border flex flex-col h-full transition-all duration-300 hover:shadow-xl ${
                  offer.featured 
                    ? 'border-[#BFA15F] border-2 relative' 
                    : 'border-slate-200 hover:border-[#0a1628]'
                }`}
              >
                {offer.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#BFA15F] text-white text-xs font-semibold px-4 py-1 uppercase tracking-wider">
                    Recommandé
                  </div>
                )}
                
                {/* Header */}
                <div className="p-8 border-b border-slate-100">
                  <div className="w-14 h-14 flex items-center justify-center bg-slate-50 text-[#0a1628] mb-6">
                    <offer.icon size={28} strokeWidth={1.5} />
                  </div>
                  <p className="text-xs uppercase tracking-wider text-[#BFA15F] font-semibold mb-2">
                    Offre {offer.id}
                  </p>
                  <h3 className="font-heading font-bold text-2xl text-[#0a1628] mb-1">
                    {offer.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">{offer.subtitle}</p>
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium">
                    {offer.format}
                  </span>
                </div>

                {/* Content */}
                <div className="p-8 flex-1">
                  <p className="body-text mb-6">{offer.description}</p>
                  
                  {/* Features */}
                  <div className="space-y-4">
                    {Array.isArray(offer.features) && typeof offer.features[0] === 'string' ? (
                      <ul className="space-y-2">
                        {offer.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-slate-600">
                            <Check size={16} className="mr-2 mt-0.5 text-[#BFA15F] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      offer.features.map((section, idx) => (
                        <div key={idx} className="mb-4">
                          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                            {section.title}
                            {section.duration && <span className="text-[#BFA15F] ml-2">({section.duration})</span>}
                          </p>
                          <ul className="space-y-1">
                            {section.items.map((item, itemIdx) => (
                              <li key={itemIdx} className="flex items-start text-sm text-slate-600">
                                <span className="w-1 h-1 rounded-full bg-[#BFA15F] mr-2 mt-2 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="p-8 pt-0 mt-auto">
                  <div className="border-t border-slate-100 pt-6">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Livrables</p>
                    <ul className="space-y-2 mb-6">
                      {offer.deliverables.map((deliverable, idx) => (
                        <li key={idx} className="flex items-start text-sm text-slate-700 font-medium">
                          <Check size={14} className="mr-2 mt-0.5 text-[#0a1628] flex-shrink-0" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                    <a 
                      href={`mailto:yoann.cohen@wyco.fr?subject=${encodeURIComponent(offer.title + ' - Demande d\'information')}`}
                      className={`w-full ${offer.featured ? 'btn-gold' : 'btn-secondary'}`}
                    >
                      {offer.cta}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption-text text-[#BFA15F] mb-4">Approche</p>
              <h2 className="heading-lg mb-6">
                Pourquoi me faire<br />
                <span className="text-slate-400">confiance sur l'IA ?</span>
              </h2>
              <div className="divider mb-8" />
              <p className="body-large mb-6">
                L'IA n'est pas qu'une question de technologie. C'est avant tout 
                un projet de transformation qui nécessite méthode, pilotage et 
                accompagnement du changement.
              </p>
              <p className="body-text mb-8">
                Fort de mon expérience en transformation d'entreprise et en pilotage 
                de programmes complexes, j'apporte une vision orientée valeur et résultats, 
                pas uniquement technologie.
              </p>
              <Link to="/articles/reussir-projet-ia-creer-valeur" className="btn-secondary">
                Lire mon article sur les projets IA
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-slate-50 border border-slate-100">
                <Users size={24} className="text-[#BFA15F] mb-4" />
                <h4 className="font-heading font-semibold mb-2">Centré métier</h4>
                <p className="text-sm text-slate-600">Partir des besoins réels, pas de la technologie</p>
              </div>
              <div className="p-6 bg-slate-50 border border-slate-100">
                <BarChart3 size={24} className="text-[#BFA15F] mb-4" />
                <h4 className="font-heading font-semibold mb-2">Orienté ROI</h4>
                <p className="text-sm text-slate-600">Mesurer la valeur créée à chaque étape</p>
              </div>
              <div className="p-6 bg-slate-50 border border-slate-100">
                <Cog size={24} className="text-[#BFA15F] mb-4" />
                <h4 className="font-heading font-semibold mb-2">Pragmatique</h4>
                <p className="text-sm text-slate-600">Quick wins d'abord, scale ensuite</p>
              </div>
              <div className="p-6 bg-slate-50 border border-slate-100">
                <Target size={24} className="text-[#BFA15F] mb-4" />
                <h4 className="font-heading font-semibold mb-2">Structuré</h4>
                <p className="text-sm text-slate-600">Gouvernance et pilotage dans la durée</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a1628] text-white">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6">
            Prêt à explorer le potentiel de l'IA ?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-10">
            Échangeons sur vos enjeux et identifions ensemble 
            les premières opportunités pour votre organisation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="btn-gold">
              Parlons de vos enjeux
              <ArrowRight size={16} className="ml-2" />
            </Link>
            <a 
              href="mailto:yoann.cohen@wyco.fr" 
              data-testid="ia-contact-btn"
              className="btn-secondary border-white/30 text-white hover:bg-white hover:text-[#0a1628]"
            >
              Me contacter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IAPage;
